import React from "react";
import { Button } from "./ui/button";
import { ArrowRight, Download } from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/9c554c17-dcc5-42ed-b044-a1cbb85f7111/images/df97baff9f7ef45616cee8289a017e3ce8fd3713cca1766e34254ab90564171e.png";

const RESUME_URL =
  "https://customer-assets.emergentagent.com/job_psoft-consulting/artifacts/4bcwiez1_Vijay_Rajgopalan_Resume.pdf";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          filter: "saturate(0.6) contrast(0.8)",
        }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-[#F7F5F0]/92" />
      <div aria-hidden="true" className="absolute inset-0 grain" />

      <div className="container-app relative pt-20 pb-24 md:pt-28 md:pb-32 lg:pt-36 lg:pb-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-8">
            <div className="anim-fadeUp delay-1 flex items-center gap-3 mb-8">
              <span className="hairline-strong w-12" />
              <span className="label-eyebrow" data-testid="hero-eyebrow">
                Independent Consultant · Boston, MA
              </span>
            </div>

            <h1
              className="anim-fadeUp delay-2 font-serif text-foreground text-balance"
              style={{
                fontSize: "clamp(2.4rem, 6vw, 5.25rem)",
                lineHeight: 0.98,
                letterSpacing: "-0.015em",
                fontWeight: 600,
              }}
              data-testid="hero-headline"
            >
              Twenty-five years of
              <br />
              <em className="font-normal italic" style={{ color: "#901A1E" }}>
                PeopleSoft
              </em>{" "}
              expertise,
              <br />
              built for the cloud era.
            </h1>

            <p
              className="anim-fadeUp delay-3 mt-8 max-w-2xl text-foreground/70 text-pretty"
              style={{ fontSize: "1.1rem", lineHeight: 1.7 }}
              data-testid="hero-subtitle"
            >
              Excelsior Consulting partners with public agencies, universities and
              Fortune 500 enterprises on Oracle PeopleSoft administration, version
              upgrades and AWS / OCI cloud migrations — across HCM, FSCM and
              Campus Solutions.
            </p>

            <div className="anim-fadeUp delay-4 mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollTo("contact")}
                className="group rounded-none bg-foreground text-background hover:bg-accent transition-colors h-auto px-8 py-4 text-sm tracking-[0.22em] uppercase font-medium"
                data-testid="hero-cta-consult"
              >
                Schedule a Consultation
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <a
                href={RESUME_URL}
                target="_blank"
                rel="noopener noreferrer"
                download="Vijay_Rajgopalan_Resume.pdf"
                data-testid="hero-cta-resume"
              >
                <Button
                  variant="outline"
                  className="rounded-none border-foreground/40 hover:bg-foreground hover:text-background h-auto px-8 py-4 text-sm tracking-[0.22em] uppercase font-medium w-full sm:w-auto"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Résumé
                </Button>
              </a>
            </div>
          </div>

          {/* Right column: index card with quick facts */}
          <div className="lg:col-span-4 anim-fadeUp delay-5">
            <div className="bg-white border border-border p-8 shadow-sm" data-testid="hero-fact-card">
              <div className="label-eyebrow mb-6">At a Glance</div>
              <dl className="space-y-5">
                <div>
                  <dt className="text-[11px] tracking-[0.2em] uppercase text-foreground/50">Practitioner</dt>
                  <dd className="font-serif text-2xl mt-1">Vijay Rajgopalan</dd>
                </div>
                <div className="hairline" />
                <div>
                  <dt className="text-[11px] tracking-[0.2em] uppercase text-foreground/50">Disciplines</dt>
                  <dd className="text-sm mt-1 leading-relaxed">
                    HCM · FSCM · Campus Solutions · PeopleTools 8.40–8.62
                  </dd>
                </div>
                <div className="hairline" />
                <div>
                  <dt className="text-[11px] tracking-[0.2em] uppercase text-foreground/50">Cloud</dt>
                  <dd className="text-sm mt-1">AWS / OCI / Azure</dd>
                </div>
                <div className="hairline" />
                <div>
                  <dt className="text-[11px] tracking-[0.2em] uppercase text-foreground/50">Operating System</dt>
                  <dd className="text-sm mt-1">RedHat Linux · Windows Server</dd>
                </div>
                <div className="hairline" />
                <div>
                  <dt className="text-[11px] tracking-[0.2em] uppercase text-foreground/50">Databases</dt>
                  <dd className="text-sm mt-1">Oracle / SQL Server / DB2</dd>
                </div>
                <div className="hairline" />
                <div>
                  <dt className="text-[11px] tracking-[0.2em] uppercase text-foreground/50">Certified</dt>
                  <dd className="text-sm mt-1">AWS Solutions Architect – Associate · Oracle Cloud Infrastructure 2025 Certified Architect Associate</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="mt-20 flex items-center gap-4 text-xs tracking-[0.3em] uppercase text-foreground/40" data-testid="hero-scroll-cue">
          <span className="hairline-strong w-10" />
          Scroll
        </div>
      </div>
    </section>
  );
};

export default Hero;
